import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { compose } from 'ramda';
import { useDrop } from 'react-dnd-cjs';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { CardDropField } from 'components';
import { dragItems } from 'constants/general';

import DraggableItem from './DraggableItem';

const containerId = 'campaign-state';

const placeholder = { id: -1 };

const removeOnIndex = (index) => (array) => array.filter((_, i) => i !== index);
const addOnIndex = (index, item) => (array) => [...array.slice(0, index), item, ...array.slice(index)];
const removePlaceholder = (items) => items.filter((item) => item.id !== -1);
const addPlaceholder = (items) => [...items, placeholder];
const findPlaceholderIndex = (items) => items.findIndex((item) => item.id === -1);
const replacePlaceholder = (items, newItem) =>
	items.map((item) => {
		if (item.id === -1) {
			return newItem;
		} else {
			return item;
		}
	});

const DragContainer = (props) => {
	const { onReorder, onRemove, items: originalItems } = props;
	const [items, setItems] = useState([]);

	const [{ canDrop, draggedItem }, drop] = useDrop({
		accept: dragItems.CAMPAIGN_STATE,
		drop(item) {
			if (containerId !== item.containerId) {
				handleForeignDrop(item.payload);
			}
		},
		collect: (monitor) => ({
			draggedItem: monitor.getItem(),
			canDrop: monitor.canDrop(),
		}),
	});

	// I got new items so replace them
	useEffect(() => {
		setItems(addPlaceholder(originalItems));
	}, [originalItems]);

	const prevDragStateRef = useRef();
	useEffect(() => {
		prevDragStateRef.current = {
			canDrop,
			draggedItem,
		};
	});
	const prevDragState = prevDragStateRef.current;

	// This will force resort and put placeholder on end
	useEffect(() => {
		if (prevDragState) {
			const foreignDropFinished =
				canDrop === false &&
				prevDragState.canDrop === true &&
				prevDragState.draggedItem.containerId !== containerId;

			if (foreignDropFinished) {
				setItems(addPlaceholder(originalItems));
			}
		}
	}, [setItems, canDrop, originalItems, prevDragState]);

	// Native handlers

	const handleNativeDragStart = useCallback(() => {
		setItems(removePlaceholder(items));
	}, [items]);

	const handleNativeSort = useCallback(
		(item, newIndex) => {
			const sortItems = compose(addOnIndex(newIndex, item.payload), removeOnIndex(item.index));
			setItems(sortItems(items));
		},
		[items],
	);

	const handleNativeDrop = useCallback(() => {
		onReorder(removePlaceholder(items));
	}, [items, onReorder]);

	const handleDragEnd = useCallback(
		(item, monitor) => {
			if (!monitor.didDrop()) {
				setItems(addPlaceholder(items));
			}
		},
		[items],
	);

	// Foreign handlers

	const handleForeignSort = useCallback(
		(hoverIndex) => {
			const placeholderIndex = findPlaceholderIndex(items);
			if (placeholderIndex !== hoverIndex) {
				const move = compose(addOnIndex(hoverIndex, placeholder), removeOnIndex(placeholderIndex));
				setItems(move(items));
			}
		},
		[items],
	);

	const handleForeignDrop = (newItem) => {
		onReorder(replacePlaceholder(items, newItem));
	};

	return (
		<div ref={drop}>
			<Grid container spacing={3} columnSpacing={5}>
				{items.map((item, i) => {
					return (
						<Grid size={4} key={item.id}>
							{item.id === -1 ? (
								<CardDropField
									animate={canDrop}
									dropFieldText={getLabelMessage('campaignState:dropText')}
								/>
							) : (
								<DraggableItem
									containerId={containerId}
									index={i}
									state={item}
									onRemove={onRemove}
									onDragStart={handleNativeDragStart}
									onForeignHover={handleForeignSort}
									onNativeHover={handleNativeSort}
									onDrop={handleNativeDrop}
									onDragEnd={handleDragEnd}
								/>
							)}
						</Grid>
					);
				})}
			</Grid>
		</div>
	);
};

DragContainer.propTypes = {
	items: PropTypes.arrayOf(PropTypes.object),
	onRemove: PropTypes.func,
	onReorder: PropTypes.func,
};

export default DragContainer;
